import { TenantContext } from '@trashlab/sitesapp/src/contexts/tenant-context';
import { trpcWebsiteGetOrganizationWebsiteByDomain } from '@trashlab/api/src/generated/hooks';
import type { TenantWebsite } from '@trashlab/sitesapp/src/contexts/tenant-types';
import { Loader } from '@trashlab/sitesapp/src/components/shared/loader';
import { lazy, Suspense, useEffect, useState } from 'react';
import env from '@trashlab/sitesapp/src/env';

const DevDomainPicker = lazy(() =>
  import('@trashlab/sitesapp/src/components/dev/dev-domain-picker').then(
    (m) => ({ default: m.DevDomainPicker }),
  ),
);

export const DEV_DOMAIN_KEY = 'sitesapp-dev-domain';
export const DISABLE_DEV_DOMAIN_PICKER_KEY =
  'sitesapp-disable-dev-domain-picker';

const isDevDomainPickerDisabled = () =>
  localStorage.getItem(DISABLE_DEV_DOMAIN_PICKER_KEY) === 'true';

const getEffectiveDomain = (): string => {
  if (!env.isProd || isDevDomainPickerDisabled()) {
    const devDomain = localStorage.getItem(DEV_DOMAIN_KEY);
    if (devDomain) return devDomain;
  }
  return window.location.hostname;
};

export const TenantProvider = ({ children }: { children: React.ReactNode }) => {
  const [searchParams, setSearchParams] = useState<URLSearchParams | null>(
    null,
  );
  const [editModeFromMessage, setEditModeFromMessage] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    setSearchParams(new URLSearchParams(window.location.search));
  }, []);

  // Listen for edit mode changes from parent window (iframe communication)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'EDIT_MODE_CHANGE') {
        setEditModeFromMessage(event.data.editMode);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const variantId = searchParams?.get('variantId') || undefined;
  // Use postMessage edit mode if available, otherwise fallback to URL parameter
  const editModeFromUrl = searchParams?.get('editMode') === 'true';
  const editMode =
    editModeFromMessage !== null ? editModeFromMessage : editModeFromUrl;

  const { data, isLoading, error } =
    trpcWebsiteGetOrganizationWebsiteByDomain.useQuery({
      domain: getEffectiveDomain(),
      variantId,
    });

  if (isLoading) {
    return <Loader />;
  }

  if (!data) {
    if (!env.isProd && !isDevDomainPickerDisabled()) {
      return (
        <Suspense fallback={<Loader />}>
          <DevDomainPicker />
        </Suspense>
      );
    }
    return (
      <div className="w-full h-screen flex items-center justify-center text-sm text-neutral-500">
        Failed to load website.
      </div>
    );
  }

  const isTenantWebsite = (value: typeof data): value is TenantWebsite => {
    return value != null && typeof value === 'object' && !('notFound' in value);
  };

  if (!isTenantWebsite(data)) {
    if (!env.isProd && !isDevDomainPickerDisabled()) {
      return (
        <Suspense fallback={<Loader />}>
          <DevDomainPicker />
        </Suspense>
      );
    }
    return (
      <div className="w-full h-screen flex items-center justify-center text-sm text-neutral-500">
        Website not found for this domain.
      </div>
    );
  }

  return (
    <TenantContext.Provider
      value={{
        website: data,
        isLoading,
        isPreview: data.isPreview || false,
        isEditMode: editMode && (data.isPreview || false),
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};
